import { Header, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import { useMemo, useState } from "react";
import { useDlqTable } from "../../hooks/useDlqTable";
import { DlqTable } from "./DlqTable";
import { QueueSummary } from "./QueueSummary";
import { JsonModal } from "../common/JsonModal";
import { ErrorModal } from "./ErrorModal";
import { DLQItem } from "../../lusca-search-api/generated-src";
import { decompressAndDeserializeData } from "../../helpers/compressionUtils";

interface DlqDeepDiveModalProps {
  visible: boolean;
  onDismiss: () => void;
  dlqItems: DLQItem[];
  stage: string;
}

export default function DlqDeepDiveModal({
  visible,
  onDismiss,
  dlqItems,
  stage,
}: DlqDeepDiveModalProps) {
  const {
    selectedItem,
    setSelectedItem,
    sortingColumn,
    sortingDescending,
    filteringText,
    setFilteringText,
    currentPageIndex,
    setCurrentPageIndex,
    pageSize,
    queueSummary,
    filteredItems,
    paginatedItems,
    handleSortingChange,
  } = useDlqTable(dlqItems);

  const [selectedErrorItem, setSelectedErrorItem] = useState<DLQItem | null>(null);

  const processedContent = useMemo(() => {
    if (!selectedItem) return null;
    if (selectedItem.is_malformed_json) {
      return selectedItem.processed_body;
    }
    if (typeof selectedItem.processed_body === "string") {
      try {
        return decompressAndDeserializeData(selectedItem.processed_body);
      } catch (error) {
        console.error("Failed to decompress DLQ data:", error);
        return selectedItem.processed_body;
      }
    }
    return selectedItem.processed_body;
  }, [selectedItem]);

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        size="max"
        header={<Header variant="h2">DLQ Deep Dive</Header>}
      >
        <SpaceBetween direction="vertical" size="l">
          <QueueSummary summary={queueSummary} />
          <DlqTable
            items={paginatedItems}
            allItems={filteredItems}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            filteringText={filteringText}
            onFilteringChange={setFilteringText}
            currentPageIndex={currentPageIndex}
            onPageChange={setCurrentPageIndex}
            pageSize={pageSize}
            onItemSelect={setSelectedItem}
            onErrorSelect={setSelectedErrorItem}
            filteredItemsCount={filteredItems.length}
            stage={stage}
          />
        </SpaceBetween>
      </Modal>

      {selectedItem && (
        <JsonModal
          title={selectedItem.is_malformed_json ? "Raw Message" : "Raw JSON"}
          content={processedContent}
          visible
          onDismiss={() => setSelectedItem(null)}
          isMalformedJson={selectedItem.is_malformed_json}
        />
      )}

      {selectedErrorItem && (
        <ErrorModal
          visible
          onDismiss={() => setSelectedErrorItem(null)}
          errorType={selectedErrorItem.error_type}
          errorMessage={selectedErrorItem.error_message}
          stackTrace={selectedErrorItem.error_stack_trace}
        />
      )}
    </>
  );
}
